import { Company } from 'module/api/company/types';
import { useTranslation } from 'module/hooks/use-translation';
import { SidebarMenu as Menu } from 'module/ui/components/atoms/dropdown/menu';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { Loading, PageLoading } from 'module/ui/components/atoms/loading';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Popover } from 'module/ui/components/atoms/popover';
import { FilterBar } from 'module/ui/components/molecules/filter-bar';
import { Short01 } from 'module/ui/components/styles';
import { HelperText02 } from 'module/ui/components/styles';
import { Layout } from 'module/ui/components/templates/argon/components';
import { faCalendar } from '@fortawesome/pro-duotone-svg-icons';
import {
  faEdit,
  faPage,
  faSidebar,
  faSparkles,
  faVideo,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faChevronDown,
  faEllipsisVertical,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { useParams } from '@rocketlane/rocket-router';
import {
  Button,
  EmptyState as RocketlaneEmptyState,
  Icon,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@rocketlane/ui';
import { CitationType } from 'api/citation/types';
import { PageType } from 'api/page-template/types';
import moment from 'moment';
import { useDeletePage } from 'queries/pages/use-delete-page';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { EmptyState } from 'components/atoms/empty-state';
import { MetadataTooltip } from 'components/common/metadata-tooltip';
import { useMeetingPagesRouteNavigations } from './route-navigations';
import { MeetingPagesSidePane } from './side-pane';
import { PageTitle } from './title';
import { useCurrentMeetingPage } from './use-current-meeting-page';
import { useMeetingPagesList } from './use-meeting-pages-list';
import { ChatProvider, useChatContext } from '../chat/chat-context';
import { ChatSidebarContent } from '../chat/chat-sidebar';
import { useChatTrigger } from '../chat/use-chat-trigger';
import { Document } from '../document/document';
import { OnViewCitationSourceCallback } from '../document-editor/extensions/citation/ui/types';
import { MeetingTranscript } from '../meeting-transcript/meeting-transcript';
import {
  VideoPlayerProvider,
  useVideoPlayerContext,
} from '../meeting-transcript/player-context';
import { MeetingTranscriptVideoAsSidePane } from '../meeting-transcript/video-as-side-pane';

const TabsWrapper = styled.div`
  button {
    border-right-width: 0px;
    border-left-width: 0px;
    border-top-width: 0px;
  }
`;
const LoadingWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const MeetingSidebar = styled(Layout.Sider)`
  flex: 0 0 294px !important;
  max-width: 294px !important;
  min-width: 294px !important;
  width: 294px !important;
  height: 100% !important;
  overflow: hidden !important;
  overflow-y: auto !important;
  background: var(--white-100);
  padding: var(--spacing-05);
  border-right: 1px solid var(--field-border-default);
  ${SearchInputExpanded} {
    margin-bottom: var(--spacing-05);
  }
`;
const MeetingSidebarInPopover = styled(MeetingSidebar)`
  height: 70vh !important;
`;

const MeetingPageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;

  .ant-menu-item-selected {
    background-color: var(--blue-20) !important;
  }
`;

const MeetingListItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  width: 100%;
  overflow: visible;
  justify-content: center;
`;
const MeetingListItemTitle = styled.div`
  ${Short01};
  color: var(--text-primary);
  width: 100%;
  flex-shrink: 0;
  display: block;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-height: 20px;
`;
const MeetingListItemDescription = styled.div`
  ${HelperText02};
  color: var(--text-tertiary);
  width: 100%;
  flex-shrink: 0;
`;
const SecondaryFilter = styled(FilterBar.Secondary)`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  ${FilterBar} {
    flex-shrink: 0;
    box-shadow: none;
  }
`;
const PageContent = styled.div`
  display: flex;
  height: 100%;
  flex-grow: 1;
`;
const SidebarMenu = styled(Menu)`
  .ant-menu-item {
    height: auto;
    min-height: 48px;
    display: flex;
    padding: var(--spacing-02) 0;
  }
  .ant-menu-title-content {
    flex: 1;
    min-width: 0;
    overflow: visible !important;
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }
`;

const RocketlaneEmptyStateRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: var(--spacing-09) var(--spacing-08);
`;

enum MeetingPagesTabs {
  Summary = 'summary',
  Transcript = 'transcript',
}

enum MeetingPagesRightSidebarKeys {
  AskAI = 'ask-ai',
  Conversation = 'conversation',
  Recordings = 'recordings',
}

export type MeetingPagesPermissions = {
  canReadMeetingPages: boolean;
  canWriteMeetingPages?: boolean;
  canDeleteMeetingPages: boolean;
  canReadMeetingPageTemplates: boolean;
  canCreateMeetingPageTemplates: boolean;
  canWriteMeetingPageTemplates: boolean;
  canDeleteMeetingPageTemplates: boolean;
};

interface MeetingPagesProps {
  company: Company;
  projectId?: number;
  meetingPermissions?: MeetingPagesPermissions;
  isModuleFed?: boolean;
}
const DocumentWithCitationHandler = ({
  document,
  readonly,
  isDocumentReadOnly,
  isChatOpen,
  pageType,
  sourceId,
}: {
  document: any;
  readonly: boolean;
  isDocumentReadOnly: boolean;
  isChatOpen: boolean;
  pageType: PageType;
  sourceId?: number;
}) => {
  const { moveToTime } = useVideoPlayerContext();

  const handleViewCitationSource: OnViewCitationSourceCallback = useCallback(
    (params) => {
      if (params.citationType === CitationType.Meeting && params.currentBlock) {
        const fromTime = params.currentBlock.fromTime;

        if (fromTime) {
          const timeInSeconds = parseFloat(fromTime);

          if (!isNaN(timeInSeconds)) {
            moveToTime(timeInSeconds);
          } else {
            moveToTime(0);
          }
        }
      }
    },
    [moveToTime],
  );

  return (
    <Document
      hideTitle={true}
      document={document}
      readonly={readonly}
      isDocumentReadOnly={isDocumentReadOnly}
      isChatOpen={isChatOpen}
      pageType={pageType}
      sourceId={sourceId}
      editorExtensionsConfig={{
        citationConfig: {
          onViewCitationSource: handleViewCitationSource,
        },
      }}
    />
  );
};

const MeetingPagesContent = ({
  company,
  projectId,
  meetingPermissions,
  isModuleFed = true,
}: MeetingPagesProps) => {
  const intl = useIntl();
  const { meetingId: meetingIdParam, meetingPageId: meetingPageIdParam } =
    useParams<{ meetingId?: string; meetingPageId?: string }>();

  // Use meetingId if available, otherwise use meetingPageIdParam
  const documentIdParam = meetingIdParam ?? meetingPageIdParam;
  const meetingId = Number(documentIdParam);
  const { value, onChange, clear } = useSearchValueState();
  const { meetingPages, isMeetingPagesLoading, allMeetingPages } =
    useMeetingPagesList({
      company,
      searchText: value,
    });
  const { navigateToMeetingList, navigateToMeeting, navigateToMeetingEdit } =
    useMeetingPagesRouteNavigations(projectId);
  const { meetingPage, isMeetingPageLoading } = useCurrentMeetingPage({
    company,
    meetingPages,
    isMeetingPagesLoading,
    projectId,
  });

  // Find the corresponding meeting page to get the sourceId
  const currentMeetingPage = useMemo(
    () =>
      meetingPages.find(
        (page) => page.documentId === meetingPage?.metadata.documentId,
      ),
    [meetingPages, meetingPage?.metadata.documentId],
  );

  // Resolve page identifiers — prefer the list-level page entry; fall back
  // to the full document (e.g. when the page is filtered out by search).
  const meetingPageDocumentMappingId =
    currentMeetingPage?.pageDocumentMappingId ??
    meetingPage?.metadata.documentSourceMappingModel?.pageDocumentMappingId;
  const meetingDocumentId =
    currentMeetingPage?.documentId ?? meetingPage?.metadata.documentId;

  const { handleDelete } = useDeletePage({
    pageDocumentMappingId: meetingPageDocumentMappingId,
    documentId: meetingDocumentId,
    pageType: PageType.Meeting,
    companyId: projectId ?? company.companyId,
    allPages: allMeetingPages,
    navigateToPage: navigateToMeeting,
    navigateToList: navigateToMeetingList,
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { isChatOpen, setIsChatOpen, chatData, setChatData } = useChatContext();

  useChatTrigger(); // Register the global chat trigger

  const handleChatDataUsed = useCallback(() => {
    if (chatData) {
      setChatData({
        ...chatData,
        elementContext: '', // Reset only elementContext
      });
    }
  }, [setChatData, chatData]);
  const sidebarItems = useMemo(() => {
    return meetingPages.map((meeting) => ({
      label: (
        <MeetingListItemContent>
          <MeetingListItemTitle>{meeting.title}</MeetingListItemTitle>
          <MeetingListItemDescription>
            {humanise(moment(meeting.startTime))}
          </MeetingListItemDescription>
        </MeetingListItemContent>
      ),
      key: meeting.documentId.toString(),
      icon: <FontAwesomeIcon icon={faPage} size={12} />,
    }));
  }, [meetingPages]);
  const searchPlaceholder = useTranslation('search');
  const SideBar = useMemo(() => {
    return (
      <>
        <SearchInputExpanded
          placeholder={searchPlaceholder}
          value={value}
          onChange={onChange}
          onClear={clear}
        />
        <div>
          {sidebarItems.length === 0 && value.length > 0 ? (
            <EmptyState
              title="No meetings found"
              primaryAction={
                <Button variant="secondary" onClick={clear}>
                  <FormattedMessage id="clear" />
                </Button>
              }
              size="medium"
              centerVertically
            />
          ) : (
            <SidebarMenu
              items={sidebarItems}
              onSelect={({ key }) => {
                const companyIdForNavigation = projectId ?? company.companyId;

                navigateToMeeting(companyIdForNavigation, Number(key));
              }}
              selectedKeys={[meetingId.toString() ?? '']}
              defaultOpenKeys={['meeting-pages']}
              expandIcon={<FontAwesomeIcon icon={faChevronDown} size={12} />}
              mode="inline"
            />
          )}
        </div>
      </>
    );
  }, [
    searchPlaceholder,
    value,
    onChange,
    clear,
    sidebarItems,
    meetingId,
    navigateToMeeting,
    company.companyId,
    projectId,
  ]);

  const rightSidebarItems = useMemo(() => {
    return [
      {
        key: MeetingPagesRightSidebarKeys.Recordings,
        title: <FormattedMessage id="video" />,
        icon: <Icon icon={faVideo} size={'1x'} />,
        content:
          currentMeetingPage?.sourceId != null ? (
            <MeetingTranscriptVideoAsSidePane
              meetingId={currentMeetingPage.sourceId}
            />
          ) : (
            <></>
          ),
      },
      {
        key: MeetingPagesRightSidebarKeys.AskAI,
        title: meetingPage?.metadata.title ?? 'Chat',
        icon: <Icon icon={faSparkles} size={'1x'} />,
        disableManualOpen: true,
        content: (
          <div className="chat-sidebar flex min-h-0 flex-1 flex-col overflow-hidden">
            <ChatSidebarContent
              accountId={company.companyId}
              userId={303}
              chatData={chatData}
              onChatDataUsed={handleChatDataUsed}
            />
          </div>
        ),
      },
    ];
  }, [
    chatData,
    company.companyId,
    handleChatDataUsed,
    meetingPage?.metadata.title,
    currentMeetingPage?.sourceId,
  ]);
  const [activeRightSidebarKey, setActiveRightSidebarKey] =
    useState<MeetingPagesRightSidebarKeys>();
  const openRightSideBar = useCallback((key: MeetingPagesRightSidebarKeys) => {
    setActiveRightSidebarKey(key);
    setIsSidebarOpen(false);
  }, []);

  useEffect(() => {
    if (isChatOpen) {
      openRightSideBar(MeetingPagesRightSidebarKeys.AskAI);
    }
  }, [isChatOpen, openRightSideBar]);
  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    window.dispatchEvent(new CustomEvent('closeInspector'));
  }, [setIsChatOpen]);
  const handleCloseRightSidebar = useCallback(() => {
    if (activeRightSidebarKey === MeetingPagesRightSidebarKeys.AskAI) {
      closeChat();
    }
    setActiveRightSidebarKey(undefined);
  }, [activeRightSidebarKey, closeChat]);

  // Close right sidebar and chat when switching between meeting pages
  useEffect(() => {
    setActiveRightSidebarKey(undefined);
    setIsChatOpen(false);
  }, [meetingId, setIsChatOpen]);

  const [currentTab, setCurrentTab] = useState<MeetingPagesTabs>(
    MeetingPagesTabs.Summary,
  );
  const onTabChange = useCallback(
    (value: string) => {
      setCurrentTab(value as MeetingPagesTabs);
      if (value === MeetingPagesTabs.Transcript) {
        openRightSideBar(MeetingPagesRightSidebarKeys.Recordings);
      }
    },
    [openRightSideBar],
  );

  if (isMeetingPagesLoading) {
    return <PageLoading />;
  }

  return (
    <VideoPlayerProvider
      onCurrentTimeChange={() => {
        openRightSideBar(MeetingPagesRightSidebarKeys.Recordings);
      }}
    >
      <MeetingPageWrapper>
        {isSidebarOpen && <MeetingSidebar>{SideBar}</MeetingSidebar>}

        <PageContent>
          <MainContent>
            <FilterBar>
              <FilterBar.Primary>
                {isMeetingPagesLoading ||
                allMeetingPages.length === 0 ? null : (
                  <Popover
                    content={
                      isSidebarOpen ? null : (
                        <MeetingSidebarInPopover>
                          {SideBar}
                        </MeetingSidebarInPopover>
                      )
                    }
                    placement="bottomLeft"
                    destroyTooltipOnHide
                    isDisabled={isSidebarOpen}
                    mouseEnterDelay={0.3}
                  >
                    <Button
                      variant="ghost"
                      leftIcon={<FontAwesomeIcon icon={faSidebar} size={12} />}
                      onClick={() => {
                        if (activeRightSidebarKey == null) {
                          setIsSidebarOpen(!isSidebarOpen);
                        }
                      }}
                    />
                  </Popover>
                )}
              </FilterBar.Primary>
              {meetingPage != null && !isMeetingPageLoading && (
                <PageTitle title={meetingPage.metadata.title} />
              )}
              <SecondaryFilter>
                {meetingPage != null &&
                  currentTab === MeetingPagesTabs.Summary && (
                    <>
                      <MetadataTooltip
                        updatedAt={meetingPage.metadata.updatedAt}
                        createdAt={meetingPage.metadata.createdAt}
                      />

                      {(!isModuleFed ||
                        meetingPermissions?.canWriteMeetingPages) && (
                        <Button
                          variant="primary"
                          leftIcon={<FontAwesomeIcon icon={faEdit} size={12} />}
                          onClick={() => {
                            const companyIdForNavigation =
                              projectId ?? company.companyId;

                            navigateToMeetingEdit(
                              companyIdForNavigation,
                              meetingPage.metadata.documentId,
                            );
                          }}
                        >
                          <FormattedMessage id="edit" />
                        </Button>
                      )}

                      {(!isModuleFed ||
                        meetingPermissions?.canDeleteMeetingPages) && (
                        <MoreActionsDropdown
                          menu={{
                            onClick: ({ key }) => {
                              if (key === 'delete') {
                                handleDelete();
                              }
                            },
                            items: [
                              {
                                type: 'group',
                                label: <FormattedMessage id="more_actions" />,
                                children: [
                                  {
                                    key: 'delete',
                                    label: <FormattedMessage id="delete" />,
                                    icon: <FontAwesomeIcon icon={faTrash} />,
                                    danger: true,
                                    disabled: !meetingPageDocumentMappingId,
                                  },
                                ],
                              },
                            ],
                          }}
                          triggerElement={
                            <Button
                              variant="ghost"
                              leftIcon={
                                <FontAwesomeIcon
                                  icon={faEllipsisVertical}
                                  size={12}
                                />
                              }
                            />
                          }
                        />
                      )}
                    </>
                  )}
              </SecondaryFilter>
            </FilterBar>

            {isMeetingPageLoading && meetingPages.length > 0 ? (
              <LoadingWrapper>
                <Loading size="large" />
              </LoadingWrapper>
            ) : meetingPage == null ? (
              <RocketlaneEmptyStateRoot>
                <RocketlaneEmptyState
                  icon={{ icon: faCalendar }}
                  message={intl.formatMessage({
                    id: 'meeting_page.empty_state_title',
                  })}
                  description={intl.formatMessage({
                    id: 'meeting_page.empty_state_message',
                  })}
                />
              </RocketlaneEmptyStateRoot>
            ) : (
              <>
                <TabsWrapper className="py-03 pl-09">
                  <Tabs
                    className="h-[40px] w-[768px] mx-auto border-b border-t-0 border-l-0 border-r-0 border-b-[var(--button-ghost-border)] border-solid"
                    value={currentTab}
                    onValueChange={onTabChange}
                  >
                    <TabsList>
                      <TabsTrigger
                        value={MeetingPagesTabs.Summary}
                        className="cursor-pointer"
                      >
                        <FormattedMessage id="account.meeting_pages.tabs.summary" />
                      </TabsTrigger>
                      <TabsTrigger
                        value={MeetingPagesTabs.Transcript}
                        className="cursor-pointer"
                      >
                        <FormattedMessage id="account.meeting_pages.tabs.transcript" />
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </TabsWrapper>
                {currentTab === MeetingPagesTabs.Summary && (
                  <DocumentWithCitationHandler
                    document={meetingPage}
                    readonly={true}
                    isDocumentReadOnly={true}
                    isChatOpen={false}
                    pageType={PageType.Meeting}
                    sourceId={
                      meetingPage?.metadata?.documentSourceMappingModel
                        ?.sourceId
                    }
                  />
                )}
                {currentTab === MeetingPagesTabs.Transcript && (
                  <>
                    {currentMeetingPage?.sourceId != null && (
                      <MeetingTranscript
                        meetingId={currentMeetingPage?.sourceId}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </MainContent>
        </PageContent>

        {/* <ChatSidebar
          isOpen={false}
          title={meetingPage?.metadata.title}
          accountId={company.companyId}
          userId={303}
          chatData={chatData}
          onChatDataUsed={handleChatDataUsed}
          onClose={() => setIsChatOpen(false)}
        /> */}
        <MeetingPagesSidePane
          items={rightSidebarItems}
          activeKey={activeRightSidebarKey}
          onSelect={(key) => {
            if (activeRightSidebarKey === MeetingPagesRightSidebarKeys.AskAI) {
              closeChat();
            }
            openRightSideBar(key as MeetingPagesRightSidebarKeys);
          }}
          onClose={handleCloseRightSidebar}
        />
      </MeetingPageWrapper>
    </VideoPlayerProvider>
  );
};

export const MeetingPages = ({
  company,
  projectId,
  meetingPermissions,
  isModuleFed = true,
}: MeetingPagesProps) => {
  return (
    <ChatProvider>
      <Wrapper>
        <MeetingPagesContent
          company={company}
          projectId={projectId}
          meetingPermissions={meetingPermissions}
          isModuleFed={isModuleFed}
        />
      </Wrapper>
    </ChatProvider>
  );
};
